import { useState, useEffect, useRef, useCallback } from 'react';
import { executeAIMove } from '../utils/aiBot';
import { logStuckBotDiagnostics } from '../utils/aiDiagnostics';
import { canCurrentPlayerSwap, getConsecutiveSwapCount } from '../utils/botHelpers';
import { debug } from '../utils/logger';
import { resolveRoomSettings } from '../utils/roomSettings';

/**
 * useAIPlayer — manages AI turn execution, stall prevention, and the bot watchdog.
 *
 * Architecture notes:
 * - Owns `lastExecutedAITurn` and `moveReleasedTick` (internal AI scheduling state).
 * - `moveInProgressRef` is intentionally NOT owned here — it is shared with
 *   human-move handlers (handleCellClick, handleDrop, swapMarkers) in App.js.
 * - `gameActionsRef` breaks the circular dependency: App.js defines handleCellClick /
 *   swapMarkers AFTER calling this hook, then writes them into the mutable ref so
 *   the AI callback always reads the latest version without re-subscribing.
 *
 * @param {Object} params
 * @param {string}  params.screen
 * @param {boolean} params.gameEnded
 * @param {boolean} params.isSpectator
 * @param {Array}   params.players
 * @param {number}  params.currentPlayer
 * @param {Array}   params.gameBoard
 * @param {Array}   params.markersPool
 * @param {Array}   params.moveHistory
 * @param {Object}  params.roomData
 * @param {string}  params.currentUser
 * @param {React.MutableRefObject} params.moveInProgressRef - mutex ref (owned by App.js)
 * @param {React.MutableRefObject} params.gameActionsRef   - { handleCellClick, swapMarkers }
 *
 * @returns {{ lastExecutedAITurn, setLastExecutedAITurn, aiMoveStartedAtRef, onMoveDone, handleUnstuckBot }}
 */
const BOT_FIGHT_DELAY_MS = 50;

export function useAIPlayer({
  screen,
  gameEnded,
  isSpectator,
  isBotFight,
  players,
  currentPlayer,
  gameBoard,
  markersPool,
  moveHistory,
  roomData,
  currentUser,
  moveInProgressRef,
  gameActionsRef,
}) {
  const [lastExecutedAITurn, setLastExecutedAITurn] = useState(null);
  const [moveReleasedTick, setMoveReleasedTick] = useState(0);
  const aiMoveStartedAtRef = useRef(null);
  // Generation counter: incremented each time a new AI execution starts.
  // The callback captures its generation and self-discards if a newer one has begun
  // (e.g. visibility-reset fired a second executeAIMove while the first was still pending).
  const aiExecutionCountRef = useRef(0);
  const latestTurnSnapshotRef = useRef({ players, currentPlayer, gameEnded });

  latestTurnSnapshotRef.current = { players, currentPlayer, gameEnded };

  /**
   * Called by move handlers (handleCellClick, handleDrop, swapMarkers) when the
   * mutex is released. Increments moveReleasedTick so the AI useEffect re-runs.
   * Replacing the direct `setMoveReleasedTick(t => t + 1)` call in those handlers
   * decouples them from this hook's internal state shape.
   */
  const onMoveDone = useCallback(() => {
    setMoveReleasedTick(t => t + 1);
  }, []);

  /**
   * Manual "unstuck" for the current AI player — host only.
   * Dumps a diagnostic snapshot (state + last 60 log entries) to the browser
   * console, then force-resets all stall mechanisms so the bot immediately retries.
   */
  const handleUnstuckBot = useCallback(() => {
    const cp = players[currentPlayer];
    const turnId = `${currentPlayer}-${cp?.name}`;

    logStuckBotDiagnostics({
      turnId,
      currentPlayerData: cp,
      moveInProgress: moveInProgressRef.current,
      aiMoveStartedAt: aiMoveStartedAtRef.current,
      lastExecutedAITurn,
      host: roomData?.host,
      currentUser,
      gameEnded,
      screen,
      poolSize: markersPool.length,
      players,
    });

    moveInProgressRef.current = false;
    aiMoveStartedAtRef.current = null;
    setLastExecutedAITurn(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, currentPlayer, lastExecutedAITurn, roomData, currentUser, gameEnded, screen, markersPool]);

  // ── AI turn execution ────────────────────────────────────────────────────────
  // IMPORTANT: Any connected room member may execute AI turns in online play.
  // apply_move() serializes duplicates server-side, which keeps the game moving
  // even after a timed-out human seat is converted to AI.
  //
  // STALL PREVENTION (bug #0):
  // - moveInProgressRef auto-resets after 15s timeout to prevent permanent stall
  // - Failed AI moves reset lastExecutedAITurn to allow retry
  // - A watchdog setTimeout detects stuck AI turns and forces retry
  useEffect(() => {
    if (screen !== 'game') return;
    if (gameEnded) return;
    if (isSpectator) return; // Spectators never execute AI moves
    if (!players || players.length === 0) return;

    // Ensure currentPlayer index is valid
    const validCurrentPlayer = Math.min(currentPlayer, players.length - 1);
    const currentPlayerData = players[validCurrentPlayer];

    const shouldExecuteAI = isBotFight || roomData?.players?.some(player => player.username === currentUser);

    // Create a unique identifier for this AI turn to prevent duplicate execution
    // when subscription updates trigger useEffect re-runs with the same currentPlayer
    const currentTurnId = `${validCurrentPlayer}-${players[validCurrentPlayer]?.name}`;

    // STALL PREVENTION: If moveInProgressRef has been true for too long, force-reset it.
    // This handles cases where a previous AI move callback errored or hung.
    // In bot fight mode, moves are synchronous/local so no stall is possible.
    if (!isBotFight) {
      if (moveInProgressRef.current && aiMoveStartedAtRef.current) {
        const elapsed = Date.now() - aiMoveStartedAtRef.current;
        if (elapsed > 15000) { // 15 second timeout
          debug('AI move stall detected -- force-resetting moveInProgress after 15s');
          moveInProgressRef.current = false;
          aiMoveStartedAtRef.current = null;
        } else {
          return; // Still within timeout, wait
        }
      }

      if (moveInProgressRef.current) return; // Another move is still in-flight (within timeout)
    }

    // Check if it's AI's turn AND the player has markers AND we haven't executed this turn
    if (currentPlayerData?.isAI &&
      currentPlayerData?.markers?.length > 0 &&
      shouldExecuteAI &&
      lastExecutedAITurn !== currentTurnId) {  // CRITICAL: Prevent duplicate execution

      // Mark this turn as executed BEFORE starting AI move to prevent race conditions
      setLastExecutedAITurn(currentTurnId);
      aiMoveStartedAtRef.current = Date.now();
      // Capture generation so stale callbacks can detect they've been superseded
      aiExecutionCountRef.current += 1;
      const capturedGeneration = aiExecutionCountRef.current;

      // DIAGNOSTIC: log in development if the move takes more than three seconds.
      const slowMoveLogger = setTimeout(() => {
        const elapsed = aiMoveStartedAtRef.current
          ? Math.round((Date.now() - aiMoveStartedAtRef.current) / 100) * 100
          : '?';
        debug(
          `[AI SLOW] Turn "${currentTurnId}" has not completed after ${elapsed}ms.`,
          `moveInProgress=${moveInProgressRef.current}`,
          `player=${currentPlayerData?.name}`,
          `screen=${screen}`,
          `gameEnded=${gameEnded}`,
        );
      }, 3000);

      const consecutiveSwaps = getConsecutiveSwapCount(moveHistory);
      const { swapLimitPerPlayer } = resolveRoomSettings(roomData);
      const gameState = {
        players,
        gameBoard,
        currentPlayer: validCurrentPlayer,
        markersPool,
        consecutiveSwaps,
        swapLimitPerPlayer,
        canSwap: canCurrentPlayerSwap({
          players,
          markersPool,
          moveHistory,
          consecutiveSwaps,
          currentPlayer: validCurrentPlayer,
          swapLimitPerPlayer,
        }),
      };
      const scheduledTurnState = {
        expectedCurrentPlayerIndex: validCurrentPlayer,
        expectedCurrentPlayerName: currentPlayerData?.name,
        expectedCurrentPlayerIsAI: true,
      };

      // Execute AI move with callback that handles the online game
      executeAIMove(gameState, async (decision) => {
        if (gameEnded) return;
        // STALE CALLBACK GUARD: if the visibility handler (or anything else) reset state
        // and started a newer execution while this one was pending in the 900ms delay,
        // discard this result — otherwise it could fire handleCellClick when it is no
        // longer this bot's turn (e.g. currentPlayer has advanced to the human player).
        if (aiExecutionCountRef.current !== capturedGeneration) {
          debug('[AI] Stale callback discarded — generation', capturedGeneration, 'superseded by', aiExecutionCountRef.current);
          aiMoveStartedAtRef.current = null;
          return;
        }

        const latestSnapshot = latestTurnSnapshotRef.current;
        const latestPlayers = Array.isArray(latestSnapshot.players) ? latestSnapshot.players : [];
        const latestCurrentPlayer = latestPlayers.length > 0
          ? Math.min(latestSnapshot.currentPlayer, latestPlayers.length - 1)
          : 0;
        const latestCurrentPlayerData = latestPlayers[latestCurrentPlayer];
        const latestTurnId = `${latestCurrentPlayer}-${latestCurrentPlayerData?.name}`;

        if (latestSnapshot.gameEnded || latestTurnId !== currentTurnId || !latestCurrentPlayerData?.isAI) {
          debug('[AI] Discarding callback because the turn is no longer AI-controlled:', currentTurnId, '->', latestTurnId);
          aiMoveStartedAtRef.current = null;
          setLastExecutedAITurn((prev) => (prev === currentTurnId ? null : prev));
          return;
        }

        // Validate decision has required fields
        if (!decision || !decision.action) {
          clearTimeout(slowMoveLogger);
          console.error('Bot returned invalid decision -- retrying turn:', decision);
          setLastExecutedAITurn(null);
          aiMoveStartedAtRef.current = null;
          return;
        }

        try {
          if (decision.action === 'place' && decision.position) {
            // Pass decision.markerIndex so AI uses the correct marker, not always index 0
            await gameActionsRef.current.handleCellClick(
              decision.position.row,
              decision.position.col,
              decision.markerIndex,
              scheduledTurnState
            );
          } else if (decision.action === 'swap') {
            await gameActionsRef.current.swapMarkers(decision.targetPlayerId, scheduledTurnState);
          }
        } catch (err) {
          // Stale-state errors mean the client board is behind the server.
          // Retrying immediately just spams 400s; the Realtime subscription will
          // deliver fresh state and the 5s watchdog will then force a clean retry.
          const isStaleState = err?.message === 'Invalid marker index' || err?.message === 'Cell is not available';
          if (isStaleState) {
            debug('[AI] Move rejected (stale state) -- awaiting Realtime refresh; watchdog will retry:', err?.message);
          } else {
            console.error('Bot move execution failed -- retrying turn:', err);
            setLastExecutedAITurn(null);
          }
        } finally {
          clearTimeout(slowMoveLogger);
          aiMoveStartedAtRef.current = null;
        }
      }, isBotFight ? BOT_FIGHT_DELAY_MS : 900).catch(err => {
        // executeAIMove itself rejected (uncaught in decision code or delay)
        clearTimeout(slowMoveLogger);
        console.error('Bot executor threw -- resetting turn:', err);
        aiMoveStartedAtRef.current = null;
        setLastExecutedAITurn(null);
      });
    }

    // STALL PREVENTION WATCHDOG: Set up a timeout that re-checks if AI is stuck.
    // This handles edge cases where React state updates don't trigger the useEffect,
    // including the case where moveInProgressRef is stuck true (e.g. Supabase timeout).
    let watchdog;
    if (currentPlayerData?.isAI && shouldExecuteAI) {
      watchdog = setTimeout(() => {
        if (!currentPlayerData?.isAI || gameEnded) return;
        // Case 1: moveInProgress stuck true for >5s — force-clear so retry can proceed
        const stuckInProgress = moveInProgressRef.current &&
          aiMoveStartedAtRef.current &&
          (Date.now() - aiMoveStartedAtRef.current > 5000);
        if (stuckInProgress) {
          debug('[AI Watchdog] moveInProgress stuck for >5s -- force-clearing', currentTurnId);
          moveInProgressRef.current = false;
          aiMoveStartedAtRef.current = null;
        }
        // Case 2: move is not in progress but turn hasn't advanced — retry.
        // IMPORTANT: unconditional null reset, not `prev === currentTurnId` guard.
        // The guard silently fails when lastExecutedAITurn holds the PREVIOUS turn's ID
        // (the AI never started because moveInProgress was true from the human's in-flight
        // write). In that case prev !== currentTurnId → guard returns prev unchanged →
        // no state change → no re-render → permanent freeze with no log output.
        if (!moveInProgressRef.current) {
          debug('[AI Watchdog] forcing retry for stuck/unstarted turn', currentTurnId);
          setLastExecutedAITurn(null);
        }
      }, 5000); // 5-second watchdog
    }

    return () => {
      if (watchdog) clearTimeout(watchdog);
    };
    // Justification: curated dep list — adding handler deps would restart the AI watchdog on every render.
    // moveReleasedTick: fires whenever moveInProgressRef goes false (ref change is invisible to React
    // without this state mirror). Without it, the next bot's turn freezes when the effect returned early
    // due to moveInProgress=true and the mutex release never triggered a re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, players, screen, gameEnded, markersPool, moveHistory, roomData?.host, currentUser, lastExecutedAITurn, moveReleasedTick]);

  // ── Mobile background/resume fix ─────────────────────────────────────────────
  // When the user locks the screen or switches apps, mobile browsers suspend JS timers
  // AND close the Supabase Realtime WebSocket. On resume:
  //   1. The in-flight room-state RPC/write hangs forever (dead WS)
  //      → its `finally` never runs → moveInProgressRef stays `true`
  //   2. The stall-detection only fires when the bot useEffect re-runs (needs a state change)
  //      but nothing changes on resume, so the bot is permanently frozen.
  // Fix: visibilitychange listener force-clears stale AI refs on tab resume so the
  //      bot useEffect re-evaluates and triggers a fresh move.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (screen !== 'game' || gameEnded) return;
      const wasStuck = moveInProgressRef.current || aiMoveStartedAtRef.current;
      if (wasStuck) {
        debug('[AI] Page visible again -- resetting stale AI move state');
        moveInProgressRef.current = false;
        aiMoveStartedAtRef.current = null;
        setLastExecutedAITurn(null);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [screen, gameEnded]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    lastExecutedAITurn,
    setLastExecutedAITurn,
    aiMoveStartedAtRef,
    onMoveDone,
    handleUnstuckBot,
  };
}
